import {
  addMinutesToTime,
  formatDateShort,
  formatRelativeDay,
  weekdayShort,
  type Intervention,
  type UserRef
} from "@techplanner/shared/core";
import { cleanAddress } from "./maps";

/** Majuscule sur la première lettre seulement (« mercredi 14 octobre » → « Mercredi 14 octobre »). */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${String(m).padStart(2, "0")}` : `${h} h`;
}

export function timeRange(i: Pick<Intervention, "startTime" | "durationMinutes">): string | null {
  if (!i.startTime) return null;
  const end = addMinutesToTime(i.startTime, i.durationMinutes);
  return end ? `${i.startTime} – ${end}` : i.startTime;
}

/** Titre affiché : le titre saisi, sinon le type d'intervention de la société. */
export function interventionTitle(i: Pick<Intervention, "title" | "typeName">): string {
  return i.title || i.typeName || "Intervention";
}

/** Libellé du type, pour les badges. */
export function typeLabel(i: Pick<Intervention, "typeName">): string {
  return i.typeName ?? "Sans type";
}

export function whenLabel(i: Pick<Intervention, "plannedDate">, today: string): string {
  if (!i.plannedDate) return "À planifier";
  const day = formatRelativeDay(i.plannedDate, today);
  const date = ["aujourd'hui", "demain", "hier"].includes(day) ? day : formatDateShort(i.plannedDate, i.plannedDate.slice(0, 4) !== today.slice(0, 4));
  return capitalize(date);
}

/** Moment de la journée : l'heure exacte compte peu sur le terrain, le matin / l'après-midi oui. */
export function dayMoment(i: Pick<Intervention, "plannedDate" | "startTime" | "durationMinutes">): "Matin" | "Après-midi" | "Journée" | null {
  if (!i.plannedDate) return null;
  if (!i.startTime || i.durationMinutes >= 420) return "Journée";
  return Number(i.startTime.slice(0, 2)) < 12 ? "Matin" : "Après-midi";
}

const MONTHS_SHORT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

/** Morceaux d'une date pour la colonne gauche des cartes : « Lun. » / « 19 » / « oct. » (+ année si autre que l'année en cours). */
export function dateParts(key: string, today: string): { top: string; day: string; month: string; year: string | null } {
  const [y, m, d] = key.split("-").map(Number) as [number, number, number];
  const rel = formatRelativeDay(key, today);
  const top = rel === "aujourd'hui" ? "Auj." : rel === "demain" ? "Demain" : rel === "hier" ? "Hier" : capitalize(weekdayShort(key));
  return { top, day: String(d), month: MONTHS_SHORT[m - 1]!, year: key.slice(0, 4) !== today.slice(0, 4) ? String(y) : null };
}

const LEGAL_FORMS = /^(GAEC|EARL|SCEA|SARL|SAS|SASU|EURL|SCL|GFA|SA|EI)\b\.?\s+/i;

/** « GAEC des Trois Chênes » → { form: "GAEC", rest: "des Trois Chênes" } : la forme juridique passe en petit. */
export function splitClientName(name: string): { form: string | null; rest: string } {
  const m = name.trim().match(LEGAL_FORMS);
  if (!m || !name.trim().slice(m[0].length)) return { form: null, rest: name.trim() };
  return { form: m[1]!.toUpperCase(), rest: name.trim().slice(m[0].length) };
}

/** Initiales du client sans la forme juridique ni les petits mots (« des Trois Chênes » → « TC »). */
export function clientInitials(name: string): string {
  const words = splitClientName(name)
    .rest.replace(/[’']/g, " ")
    .split(/[\s-]+/)
    .filter((w) => w && !/^(de|du|des|la|le|les|l|d|et|en|au|aux|ferme)$/i.test(w));
  if (!words.length) return initials(name);
  return (words[0]![0]! + (words[1]?.[0] ?? "")).toUpperCase();
}

/** Commune lue dans l'adresse (« Les Chênes, 72300 Sablé-sur-Sarthe » → « Sablé-sur-Sarthe »). */
export function townOf(address: string | null): string | null {
  const a = cleanAddress(address)?.trim();
  if (!a) return null;
  const m = a.match(/\b\d{5}\s+([^,\n]+)/);
  if (m) return m[1]!.replace(/\s+France$/i, "").trim();
  const parts = a.split(/[,\n]/).map((p) => p.trim()).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1]! : null;
}

const CLIENT_COLORS = ["#0d9488", "#2563eb", "#7c3aed", "#d97706", "#db2777", "#059669", "#4f46e5", "#b45309", "#0891b2", "#9333ea"];

export function clientColor(id: number): string {
  return CLIENT_COLORS[Math.abs(id) % CLIENT_COLORS.length]!;
}

/** « aujourd'hui », « demain », sinon « jeu. 15 oct. » (+ année si autre année). */
export function shortDay(key: string, today: string): string {
  const rel = formatRelativeDay(key, today);
  if (rel === "aujourd'hui" || rel === "demain" || rel === "hier") return rel;
  return formatDateShort(key, key.slice(0, 4) !== today.slice(0, 4));
}

/**
 * Nom proposé pour un nouvel équipement : le nom du type suivi du numéro suivant chez ce client
 * (« Chaudière gaz 1 », puis « Chaudière gaz 2 »…). Tient compte des numéros déjà pris.
 */
export function nextEquipmentLabel(typeName: string, typeId: number, existing: Array<{ typeId: number | null; label: string }>): string {
  const name = typeName.trim();
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const numbered = new RegExp("^" + escaped + "\\s+(\\d+)$", "i");
  let last = existing.filter((e) => e.typeId === typeId).length;
  for (const e of existing) {
    const m = e.label.trim().match(numbered);
    if (m) last = Math.max(last, Number(m[1]));
  }
  return `${name} ${last + 1}`;
}

export function technicianNames(technicians: UserRef[]): string {
  return technicians.map((t) => t.displayName).join(", ");
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) return digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
  return phone;
}

export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n > 1 ? many : one}`;
}
